'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatApi, chatStreamFetch } from '@/lib/api';
import type { ChatSession } from '@/lib/api';

interface LocalMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  timestamp?: string;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChatModal({ isOpen, onClose }: ChatModalProps) {
  const router = useRouter();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState<'qa' | 'tool'>('qa');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 열릴 때 세션 로드
  useEffect(() => {
    if (!isOpen) return;
    chatApi.getSessions(20).then(setSessions).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const handleSelectSession = async (sessionId: number) => {
    setCurrentSessionId(sessionId);
    try {
      const detail = await chatApi.getSession(sessionId);
      setMessages(detail.messages.map(m => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: m.created_at,
      })));
    } catch {
      setMessages([]);
    }
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleDeleteSession = async (sessionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await chatApi.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) handleNewChat();
    } catch { /* */ }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || isLoading) return;

    setInput('');
    // textarea 높이 리셋
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setIsLoading(true);

    const now = new Date().toISOString();
    setMessages(prev => [
      ...prev,
      { role: 'user', content, timestamp: now },
      { role: 'assistant', content: '', isStreaming: true, timestamp: now },
    ]);

    // Tool Use 모드 — 동기 호출
    if (chatMode === 'tool') {
      try {
        const result = await chatApi.sendMessage(content, currentSessionId || undefined, 'tool');
        if (result.session_id && !currentSessionId) {
          setCurrentSessionId(result.session_id);
          chatApi.getSessions(20).then(setSessions).catch(() => {});
        }

        // 도구 실행 결과 표시
        let responseText = result.message?.content || '';
        const toolResults = (result as Record<string, unknown>).tool_results as Array<{tool: string; args: Record<string, unknown>; result: string}> | undefined;
        if (toolResults && toolResults.length > 0) {
          const toolSummary = toolResults.map(tr => {
            try {
              const r = JSON.parse(tr.result);
              return `**[${tr.tool}]** ${r.message || r.count + '건 조회'}`;
            } catch {
              return `**[${tr.tool}]** 실행 완료`;
            }
          }).join('\n');
          if (!responseText) responseText = toolSummary;
        }

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: responseText, isStreaming: false, timestamp: now };
          return updated;
        });
      } catch (err) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: `Tool Use 오류: ${err}`, isStreaming: false };
          return updated;
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // QA 모드 — SSE 스트리밍
    try {
      await chatStreamFetch(
        content,
        currentSessionId,
        (text) => {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last.role === 'assistant') updated[updated.length - 1] = { ...last, content: last.content + text };
            return updated;
          });
        },
        (id) => {
          setCurrentSessionId(id);
          chatApi.getSessions(20).then(setSessions).catch(() => {});
        },
        () => {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last.role === 'assistant') updated[updated.length - 1] = { ...last, isStreaming: false };
            return updated;
          });
          setIsLoading(false);
        },
        (err) => {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last.role === 'assistant') updated[updated.length - 1] = { ...last, content: `오류: ${err}`, isStreaming: false };
            return updated;
          });
          setIsLoading(false);
        }
      );
    } catch {
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last.role === 'assistant') updated[updated.length - 1] = { ...last, content: 'AI 연결에 실패했습니다.', isStreaming: false };
        return updated;
      });
      setIsLoading(false);
    }
  };

  const handleLinkClick = useCallback((href: string) => {
    onClose();
    setTimeout(() => router.push(href), 150);
  }, [onClose, router]);

  // 마크다운 내 내부 링크 감지
  const internalLinkRegex = /\/(?:analysis|design|development|deployment|project|reports|menu-structure|settings|implementation|closing|management)[/\w-]*/;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markdownComponents: Record<string, React.FC<any>> = {
    // 테이블
    table: ({ children }: { children: React.ReactNode }) => (
      <div className="overflow-x-auto my-2">
        <table className="w-full text-xs border-collapse border border-gray-200 rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode }) => (
      <thead className="bg-gray-100 text-gray-700 font-semibold">{children}</thead>
    ),
    th: ({ children }: { children: React.ReactNode }) => (
      <th className="px-3 py-2 border border-gray-200 text-left text-xs">{children}</th>
    ),
    td: ({ children }: { children: React.ReactNode }) => (
      <td className="px-3 py-1.5 border border-gray-200 text-xs">{children}</td>
    ),
    // 리스트
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>
    ),
    // 볼드/헤딩
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 className="text-base font-bold text-gray-900 mt-3 mb-1">{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 className="text-sm font-bold text-gray-900 mt-2 mb-1">{children}</h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 className="text-sm font-semibold text-gray-800 mt-2 mb-1">{children}</h3>
    ),
    // 코드
    code: ({ children, className }: { children: React.ReactNode; className?: string }) => {
      const isBlock = className?.includes('language-');
      return isBlock ? (
        <pre className="bg-gray-800 text-gray-100 rounded-lg px-3 py-2 my-2 overflow-x-auto text-xs">
          <code>{children}</code>
        </pre>
      ) : (
        <code className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-xs">{children}</code>
      );
    },
    // 링크 - 내부 경로면 라우터 이동
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => {
      if (href && internalLinkRegex.test(href)) {
        return (
          <button onClick={() => handleLinkClick(href)}
            className="text-blue-600 hover:text-blue-800 underline underline-offset-2 font-medium">
            {children}
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{children}</a>;
    },
    // 단락
    p: ({ children }: { children: React.ReactNode }) => (
      <p className="my-1">{children}</p>
    ),
    // 구분선
    hr: () => <hr className="my-2 border-gray-200" />,
  };

  const renderMarkdown = (content: string) => {
    // 내부 경로를 마크다운 링크로 변환 (plain text 경로 → 클릭 가능)
    const processed = content.replace(
      /(\/(?:analysis|design|development|deployment|project|reports|menu-structure|settings|implementation|closing|management)[/\w-]*)/g,
      '[$1]($1)'
    );
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {processed}
      </ReactMarkdown>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 텍스트에어리어 자동 높이
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* 배경 */}
      <div className="fixed inset-0 bg-black/30 z-[60]" onClick={onClose} />

      {/* 패널 */}
      <div className="fixed inset-4 z-[61] flex rounded-2xl overflow-hidden shadow-2xl bg-white"
        style={{ maxWidth: '1100px', maxHeight: '85vh', margin: 'auto' }}>

        {/* 좌측: 세션 히스토리 */}
        <div className="w-64 bg-[#0F172A] flex flex-col flex-shrink-0">
          {/* 좌측 상단: 모드 전환 */}
          <div className="px-4 py-3 border-b border-white/10 space-y-2">
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              <button
                onClick={() => setChatMode('qa')}
                className={`flex-1 py-1.5 text-[11px] font-medium transition ${
                  chatMode === 'qa' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                질의응답
              </button>
              <button
                onClick={() => setChatMode('tool')}
                className={`flex-1 py-1.5 text-[11px] font-medium transition ${
                  chatMode === 'tool' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Tool Use
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center">
              {chatMode === 'qa' ? '프로젝트 데이터 조회/분석' : '데이터 등록/수정/삭제 지시'}
            </p>
          </div>

          {/* 세션 목록 */}
          <div className="flex-1 overflow-y-auto py-2">
            {sessions.length === 0 ? (
              <p className="px-4 py-6 text-center text-gray-400 text-xs">대화 기록이 없습니다</p>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => handleSelectSession(s.id)}
                  className={`group mx-2 mb-0.5 px-3 py-2.5 rounded-lg cursor-pointer transition ${
                    currentSessionId === s.id
                      ? 'bg-white/10 text-white'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate text-gray-200">{s.title || '새 대화'}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{s.message_count}개 메시지</p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-500 hover:text-red-400 transition flex-shrink-0 mt-0.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 좌측 하단: 새 대화 */}
          <div className="px-4 py-3 border-t border-white/10">
            <button
              onClick={handleNewChat}
              className="w-full px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition flex items-center justify-center gap-2 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              새 대화
            </button>
          </div>
        </div>

        {/* 우측: 대화 영역 */}
        <div className="flex-1 flex flex-col bg-white">
          {/* 우측 헤더 */}
          <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0" />
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-800">OmniMind 어시스턴트</h2>
                <p className="text-xs text-gray-400">
                  {chatMode === 'qa' ? '프로젝트 데이터 기반 질의응답' : 'Tool Use - 데이터 등록/수정 지시'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 메시지 */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-lg">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-gray-700 mb-1">
                    {chatMode === 'qa' ? '무엇을 도와드릴까요?' : '어떤 작업을 지시할까요?'}
                  </h3>
                  <p className="text-sm text-gray-400 mb-6">
                    {chatMode === 'qa'
                      ? 'WBS, 산출물, 요구사항, 공정율 등 프로젝트 데이터를 기반으로 답변합니다.'
                      : 'AI가 도구를 호출하여 데이터를 직접 등록/수정합니다.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {(chatMode === 'qa' ? [
                      '전체 공정율과 단계별 현황',
                      '설계 단계 산출물 현황 알려줘',
                      '미완료 요구사항 목록 보여줘',
                      '이번 주 진행 상황 요약해줘',
                    ] : [
                      '요구사항에 "사용자 프로필 관리" 추가해줘',
                      '설계 단계에 UI 검수 작업 추가해줘',
                      'SFR-001 요구사항 상태를 수용으로 변경해줘',
                      '메뉴에 "공지사항" 추가해줘',
                    ]).map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); inputRef.current?.focus(); }}
                        className="px-3 py-2.5 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-xl hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition text-left"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    {/* 아바타 */}
                    {msg.role === 'user' ? (
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold bg-blue-100 text-blue-600">U</div>
                    ) : (
                      <img src="/omnimind-icon.png" alt="AI" className="flex-shrink-0 w-8 h-8 rounded-lg" />
                    )}
                    {/* 내용 */}
                    <div className={`flex-1 max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                      <div className={`inline-block text-left px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-tr-sm'
                          : 'bg-gray-50 border border-gray-100 text-gray-800 rounded-tl-sm'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <div className="prose-chat">
                            {msg.content ? renderMarkdown(msg.content) : (msg.isStreaming ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-400 py-1">
                                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </span>
                            ) : '')}
                            {msg.isStreaming && msg.content && (
                              <span className="inline-block w-0.5 h-4 bg-blue-400 animate-pulse ml-0.5 align-middle" />
                            )}
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* 입력 영역 */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={3}
                placeholder="프로젝트에 대해 질문하세요...&#10;Shift+Enter로 줄바꿈, Enter로 전송"
                className="w-full px-4 pt-3 pb-1 resize-none focus:outline-none text-sm text-gray-800 placeholder-gray-400"
                style={{ minHeight: '80px', maxHeight: '200px' }}
                disabled={isLoading}
              />
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Enter</kbd>
                  <span>전송</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono ml-2">Shift+Enter</kbd>
                  <span>줄바꿈</span>
                </div>
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-40 flex items-center gap-2 font-medium"
                >
                  {isLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      응답 중
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                      </svg>
                      전송
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
